import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { inputBaseClass, inputTransparentClass, inputFocusRingClass, inputInvalidClass, type InputVariant } from './input';

export interface SearchInputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
	onClear?: () => void;
	variant?: InputVariant;
}

/**
 * SearchInput component with built-in clear functionality
 * - Shows clear button (X) when input has value
 * - Clears input when ESC key is pressed
 * - Clears input when clear button is clicked
 */
const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
	({ className, value, onChange, onClear, onKeyDown, variant = 'default', ...props }, ref) => {
		const hasValue = Boolean(value && String(value).length > 0);

		const handleClear = React.useCallback(() => {
			if (onChange) {
				// Create a synthetic event to trigger onChange
				const syntheticEvent = {
					target: { value: '' },
					currentTarget: { value: '' }
				} as React.ChangeEvent<HTMLInputElement>;
				onChange(syntheticEvent);
			}
			if (onClear) {
				onClear();
			}
		}, [onChange, onClear]);

		const handleKeyDown = React.useCallback(
			(e: React.KeyboardEvent<HTMLInputElement>) => {
				if (e.key === 'Escape' && hasValue) {
					e.preventDefault();
					handleClear();
				}
				if (onKeyDown) {
					onKeyDown(e);
				}
			},
			[hasValue, handleClear, onKeyDown]
		);

		return (
			<div className="relative">
				<input
					ref={ref}
					type="text"
					data-slot="input"
					className={cn(
						inputBaseClass,
						variant === 'transparent' && inputTransparentClass,
						inputFocusRingClass,
						inputInvalidClass,
						hasValue && 'pr-9', // Add padding for clear button
						className
					)}
					value={value}
					onChange={onChange}
					onKeyDown={handleKeyDown}
					{...props}
				/>
				{hasValue && (
					<button
						type="button"
						onClick={handleClear}
						className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
						aria-label="Clear search"
						tabIndex={-1}
					>
						<X className="h-4 w-4" />
					</button>
				)}
			</div>
		);
	}
);

SearchInput.displayName = 'SearchInput';

export { SearchInput };
